import { traceTelemetry } from '../domain/trace-telemetry';
import { useMemo, type RefObject, type CSSProperties } from 'react';
import type { SocialVideoPresentation } from '../../../renderer/src/social-presentation';
import { compositionOverlayUnit, socialPresentationInsets } from '../../../renderer/src/social-geometry';
import { recordingDeviceLabel } from '../../../renderer/src/recording-device';
import { journeyProgressAtFilm, cameraPhaseAtFilm } from '../domain/camera-plan';
import { compileRideProgress, formatDuration, type Ride } from '../domain/ride';
import { compileRideMetrics } from '../domain/ride-metrics-plan';
import { compileRideSpeed } from '../domain/ride-speed-plan';
import { measurementUnits, type UnitSystem } from '../domain/measurement-units';
import { ElevationProfile } from './ElevationProfile';
import { MetricNumerals } from './MetricNumerals';
import { SocialStatistic } from './SocialStatistic';
import { VIDEO_WEBSITE } from '../config/video-brand';
import './social-composition.css';

export interface SocialCompositionProperties {
  ride: Ride; progress: number; durationSeconds: number; unitSystem: UnitSystem;
  presentation: SocialVideoPresentation; width: number; height: number;
  attributionTarget?: RefObject<HTMLDivElement | null> | undefined;
  attributionHtml?: string | undefined;
  overlayTarget?: RefObject<HTMLDivElement | null> | undefined;
}

/** Shared edge plates used over the live map and the chooser's single map still. */
export function SocialComposition({ ride, progress, durationSeconds, unitSystem, presentation, width, height,
  attributionTarget, attributionHtml, overlayTarget }: SocialCompositionProperties) {
  const telemetry = useMemo(() => traceTelemetry(ride), [ride]);
  const measured = (value: string) => telemetry.available ? value : '—';
  const units = measurementUnits(unitSystem);
  const plan = useMemo(() => ({ metrics: telemetry.metrics,
    route: compileRideProgress(telemetry.points), metricsAt: compileRideMetrics(telemetry.points, ride.timerDurationSeconds),
    speedAt: compileRideSpeed(ride.sourcePoints ?? telemetry.points) }), [ride, telemetry]);
  const journey = journeyProgressAtFilm(progress, durationSeconds);
  const live = plan.metricsAt(plan.route.at(journey));
  const metrics = plan.metrics;
  const speed = plan.speedAt(plan.route.pointAt(journey).recordedAt);
  const insets = socialPresentationInsets(presentation, width, height);
  const unit = compositionOverlayUnit(width, height);
  const style = { '--social-unit': `${unit}px`, '--title-size': `${presentation.titleSize * unit}px`,
    '--statistics-size': `${presentation.statisticsSize * unit}px`, '--extended-scale': presentation.extendedNumeralScale / 100,
    '--plate-opacity': 1 - presentation.plateTransparency / 100, '--info-color': presentation.textColor,
    '--left-inset': `${insets.left}px`, '--right-inset': `${insets.right}px`, '--top-inset': `${insets.top}px`,
    '--footer-inset': `${insets.bottom}px`, '--card-right': `${Math.max(insets.right, 372 * unit)}px`,
  } as CSSProperties;
  const phase = cameraPhaseAtFilm(progress, durationSeconds);
  const title = <div className="social-title social-plate" data-composition-element="Title plate">
    <div className="social-brand"><span className="social-route-state">{Math.round(journey * 100).toString().padStart(2, '0')}% {phase === 'opening' ? 'APPROACH' : phase === 'finish' ? 'COMPLETE' : 'PLAYING'}</span>
      <span className="social-website">{VIDEO_WEBSITE}</span></div>
    <h1>{ride.title}</h1>
    {ride.recordingDevice && <p className="social-device">{recordingDeviceLabel(ride.recordingDevice)}</p>}
  </div>;
  const statistics = <dl className="social-statistics social-plate" data-composition-element="Statistics plate">
    <SocialStatistic label="Distance" value={units.distance(live.distanceKilometers).toFixed(1)}
      total={units.distance(metrics.distanceKilometers).toFixed(1)} kind="decimal" unit={units.distanceUnit} presentation={presentation} />
    <SocialStatistic label="Trace time" value={measured(formatDuration(live.durationSeconds))}
      total={measured(formatDuration(metrics.durationSeconds))} kind="duration" presentation={presentation} />
    <SocialStatistic label="Ascent" value={measured(Math.round(units.elevation(live.ascentMeters)).toLocaleString('en-US'))}
      total={measured(Math.round(units.elevation(metrics.ascentMeters)).toLocaleString('en-US'))} kind="integer" unit={units.elevationUnit} presentation={presentation} />
    <SocialStatistic label="Speed" value={measured(units.speed(speed).toFixed(1))}
      total={measured(units.speed(metrics.averageKilometersPerHour).toFixed(1))} kind="decimal" unit={units.speedUnit} totalLabel="trace avg" presentation={presentation} />
  </dl>;
  return <div ref={overlayTarget} className="social-composition" data-social-layout={presentation.layout}
    data-landscape={width > height || undefined} data-elevation={presentation.showElevation}
    data-action-rail={insets.right > 64 * unit} style={style}>
    {presentation.layout === 'information-card' ? <div className="social-information-card social-plate" data-composition-element="Information card">{title}{statistics}</div>
      : presentation.layout !== 'lower-title' && title}
    <div className="social-footer">
      {presentation.layout === 'lower-title' && title}
      {presentation.layout !== 'information-card' && statistics}
      {presentation.showElevation && <div className="social-elevation social-plate" data-composition-element="Elevation">
        <p><span>Elevation</span><span>{!telemetry.available ? '—' : `${Math.round(units.elevation(metrics.minimumElevationMeters))}–${Math.round(units.elevation(metrics.maximumElevationMeters))} ${units.elevationUnit}`}</span></p>
        <div style={!telemetry.available ? { visibility: 'hidden' } : undefined}><ElevationProfile points={telemetry.points} progress={journey} unitSystem={unitSystem} /></div>
      </div>}
      <div className="social-playback social-plate" data-composition-element="Playback progress">
        <MetricNumerals value={measured(formatDuration(live.durationSeconds))} kind="duration" />
        <div className="social-progress-track"><span style={{ width: `${progress * 100}%` }} /></div>
      </div>
      <div className="social-credits social-plate video-map-credits" data-composition-element="Map attribution" ref={attributionTarget}
        {...(attributionHtml ? { dangerouslySetInnerHTML: { __html: attributionHtml } } : {})} />
    </div>
  </div>;
}
